import { db, type DocumentoStorageBackend } from './db.js'

const OPFS_DIR = 'documentos'

export function isOpfsSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'storage' in navigator &&
    typeof navigator.storage.getDirectory === 'function'
  )
}

async function getDocumentsDir(): Promise<FileSystemDirectoryHandle | null> {
  if (!isOpfsSupported()) return null
  try {
    const root = await navigator.storage.getDirectory()
    return root.getDirectoryHandle(OPFS_DIR, { create: true })
  } catch {
    return null
  }
}

export async function writeDocumentBlob(id: number, blob: Blob): Promise<DocumentoStorageBackend> {
  const dir = await getDocumentsDir()
  if (dir) {
    try {
      const handle = await dir.getFileHandle(String(id), { create: true })
      const writable = await handle.createWritable()
      await writable.write(blob)
      await writable.close()
      await db.documento_arquivos.delete(id).catch(() => undefined)
      return 'opfs'
    } catch {
      /* fallback abaixo */
    }
  }
  await db.documento_arquivos.put({ id, blob })
  return 'indexeddb'
}

export async function readDocumentBlob(
  id: number,
  backend?: DocumentoStorageBackend,
): Promise<Blob | null> {
  if (backend !== 'indexeddb') {
    const dir = await getDocumentsDir()
    if (dir) {
      try {
        const handle = await dir.getFileHandle(String(id))
        return handle.getFile()
      } catch {
        /* tenta fallback */
      }
    }
  }

  if (backend !== 'opfs') {
    const row = await db.documento_arquivos.get(id)
    if (row?.blob) return row.blob
  }

  return null
}

export async function deleteDocumentBlob(
  id: number,
  backend?: DocumentoStorageBackend,
): Promise<void> {
  await db.documento_arquivos.delete(id).catch(() => undefined)

  if (backend === 'indexeddb') return

  const dir = await getDocumentsDir()
  if (!dir) return
  try {
    await dir.removeEntry(String(id))
  } catch {
    /* arquivo já removido */
  }
}

export async function clearAllDocumentFiles(): Promise<void> {
  await db.documento_arquivos.clear()
  const dir = await getDocumentsDir()
  if (!dir) return
  try {
    for await (const [name] of dir.entries()) {
      await dir.removeEntry(name).catch(() => undefined)
    }
  } catch {
    /* noop */
  }
}

export async function getDocumentosStorageBytes(): Promise<number> {
  const docs = await db.documentos.toArray()
  return docs.reduce((total, doc) => {
    if (!doc.hasArquivo) return total
    return total + (doc.tamanho ?? 0)
  }, 0)
}

export async function restoreDocumentoArquivo(
  id: number,
  blob: Blob,
  tipo: string,
  tamanho?: number,
): Promise<void> {
  const backend = await writeDocumentBlob(id, blob)
  await db.documentos.update(id, {
    arquivoBackend: backend,
    hasArquivo: true,
    tipo: tipo || blob.type || 'application/octet-stream',
    tamanho: tamanho ?? blob.size,
  })
}
